<template>
  <div class="dashboard" v-loading="admin.loading.value">
    <!-- 核心指标 -->
    <div class="stat-grid">
      <div v-for="card in kpis" :key="card.label" class="stat-card kpi-card" :style="{ '--kpi': card.color }">
        <span class="kpi-icon">
          <el-icon :size="16"><component :is="card.icon" /></el-icon>
        </span>
        <span>{{ card.label }}</span>
        <strong>{{ card.value }}</strong>
        <small>{{ card.note }}</small>
      </div>
    </div>

    <!-- 中部图表 -->
    <div class="board-grid">
      <section class="content-panel">
        <div class="section-head">
          <div>
            <h2>客户设备分布</h2>
            <p>按设备数 TOP 6</p>
          </div>
        </div>
        <div class="panel-body">
          <ul class="bars">
            <li v-for="item in deviceTop" :key="item.id">
              <span class="bar-label">{{ admin.customerName(item.id) }}</span>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: barWidth(item.deviceCount) }"></div>
              </div>
              <span class="bar-value">{{ item.deviceCount }}</span>
            </li>
            <li v-if="!deviceTop.length" class="empty">暂无客户数据</li>
          </ul>
        </div>
      </section>

      <section class="content-panel">
        <div class="section-head">
          <div>
            <h2>运营状态概览</h2>
            <p>客户账户与广告状态</p>
          </div>
        </div>
        <div class="panel-body">
          <div class="ring-wrap">
            <svg class="ring" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" class="ring-bg" />
              <circle cx="60" cy="60" r="50" class="ring-fg" :stroke-dasharray="ringDash(stats.normal)" />
            </svg>
            <div class="ring-center">
              <strong>{{ stats.total }}</strong>
              <span>客户总数</span>
            </div>
          </div>
          <ul class="legend">
            <li><i style="background: #10b981"></i>正常<b>{{ stats.normal }}</b></li>
            <li><i style="background: #f87171"></i>禁用<b>{{ stats.disabled }}</b></li>
            <li><i style="background: #3b82f6"></i>投放中<b>{{ stats.active }}</b></li>
            <li><i style="background: #94a3b8"></i>已暂停<b>{{ stats.paused }}</b></li>
          </ul>
        </div>
      </section>

      <section class="content-panel">
        <div class="section-head">
          <div>
            <h2>客户额度排行</h2>
            <p>按剩余额度 TOP 6</p>
          </div>
        </div>
        <div class="panel-body">
          <ul class="bars">
            <li v-for="item in balanceTop" :key="item.id">
              <span class="bar-label">{{ admin.customerName(item.id) }}</span>
              <div class="bar-track">
                <div class="bar-fill gold" :style="{ width: balanceWidth(item.balance) }"></div>
              </div>
              <span class="bar-value">{{ fmt(item.balance, 0) }} 次</span>
            </li>
            <li v-if="!balanceTop.length" class="empty">暂无客户数据</li>
          </ul>
        </div>
      </section>
    </div>

    <!-- 底部动态 -->
    <div class="board-grid two">
      <section class="content-panel">
        <div class="section-head">
          <div>
            <h2>最近广告记录</h2>
            <p>最新 6 条</p>
          </div>
        </div>
        <div class="panel-body">
          <ul class="feed">
            <li v-for="(item, index) in recentRecords" :key="index">
              <span class="dot success"></span>
              <div class="feed-main">
                <strong>{{ admin.customerName(item.customerId) }}</strong>
                <small>{{ adLabel(item.adType) }} · {{ adPlatformLabel(item.adPlatform) }} · {{ formatReward(item.reward, item.userRevenueRatio) }} 金币</small>
              </div>
              <time>{{ timeOf(item.createdAt) }}</time>
            </li>
            <li v-if="!recentRecords.length" class="empty">暂无广告记录</li>
          </ul>
        </div>
      </section>

      <section class="content-panel">
        <div class="section-head">
          <div>
            <h2>最近风控记录</h2>
            <p>最新 6 条</p>
          </div>
        </div>
        <div class="panel-body">
          <ul class="feed">
            <li v-for="(item, index) in recentRisks" :key="index">
              <span class="dot" :class="item.passed === 1 ? 'success' : 'danger'"></span>
              <div class="feed-main">
                <strong>{{ admin.customerName(item.customerId) }}</strong>
                <small>{{ item.reasonMessage || item.reasonCode || '-' }}</small>
              </div>
              <time>{{ timeOf(item.createdAt) }}</time>
            </li>
            <li v-if="!recentRisks.length" class="empty">暂无风控记录</li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { User, Iphone, Grid, Wallet } from '@element-plus/icons-vue'
import { formatReward } from '@/utils/reward'
import { adPlatformLabel } from '@/utils/adPlatform'

const props = defineProps({ admin: Object })

/* ---- 数据源 ---- */
const summary = computed(() => props.admin.summary)
const totalDevices = computed(() => Number(summary.value.totalDevices || 0))
const totalBalance = computed(() => Number(summary.value.totalBalance || 0))

/* ---- 数字滚动动画 ---- */
function useAnimatedNumber(getter, digits = 0) {
  const display = ref(0)
  let raf = 0
  const update = (target) => {
    cancelAnimationFrame(raf)
    const from = display.value
    const start = performance.now()
    const dur = 800
    const step = (t) => {
      const p = Math.min((t - start) / dur, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      const value = from + (target - from) * eased
      display.value = digits ? value : Math.round(value)
      if (p < 1) raf = requestAnimationFrame(step)
      else display.value = target
    }
    raf = requestAnimationFrame(step)
  }
  watch(getter, update, { immediate: true })
  onBeforeUnmount(() => cancelAnimationFrame(raf))
  return display
}

const customerCount = useAnimatedNumber(() => Number(summary.value.totalCustomers || 0))
const deviceCount = useAnimatedNumber(() => totalDevices.value)
const appCount = useAnimatedNumber(() => Number(summary.value.totalApps || 0))
const balance = useAnimatedNumber(() => totalBalance.value, 2)

const fmt = (n, digits = 0) => Number(n || 0).toLocaleString('zh-CN', {
  minimumFractionDigits: digits, maximumFractionDigits: digits
})

/* ---- 核心指标 ---- */
const kpis = computed(() => [
  { label: '客户总数', note: '已录入客户', value: fmt(customerCount.value), color: '#3b82f6', icon: User },
  { label: '设备总数', note: '全部客户设备', value: fmt(deviceCount.value), color: '#10b981', icon: Iphone },
  { label: '应用总数', note: '已配置应用', value: fmt(appCount.value), color: '#0d9488', icon: Grid },
  { label: '总可用额度', note: '客户额度合计（次）', value: `${fmt(balance.value, 0)} 次`, color: '#f59e0b', icon: Wallet }
])

/* ---- 客户设备分布 ---- */
const deviceTop = computed(() => summary.value.topByDevices || [])
const maxDevices = computed(() => Math.max(1, ...deviceTop.value.map((c) => Number(c.deviceCount || 0))))
const barWidth = (v) => `${(Number(v || 0) / maxDevices.value) * 100}%`

/* ---- 运营状态概览 ---- */
const stats = computed(() => ({
  total: Number(summary.value.totalCustomers || 0),
  normal: Number(summary.value.normalCustomers || 0),
  disabled: Number(summary.value.disabledCustomers || 0),
  active: Number(summary.value.activeAdCustomers || 0),
  paused: Number(summary.value.pausedAdCustomers || 0)
}))
const RING_C = 2 * Math.PI * 50
const ringDash = (v) => `${(v / (stats.value.total || 1)) * RING_C} ${RING_C}`

/* ---- 客户额度排行 ---- */
const balanceTop = computed(() => summary.value.topByBalance || [])
const maxBalance = computed(() => Math.max(1, ...balanceTop.value.map((c) => Number(c.balance || 0))))
const balanceWidth = (v) => `${(Number(v || 0) / maxBalance.value) * 100}%`

/* ---- 最近动态 ---- */
const recentRecords = computed(() => props.admin.records.value.slice(0, 6))
const recentRisks = computed(() => props.admin.riskLogs.value.slice(0, 6))
const adLabel = (type) => ({ reward: '激励视频', interstitial: '插屏', banner: 'Banner' }[type] || type || '-')
const timeOf = (t) => (t ? String(t).slice(5, 16).replace('T', ' ') : '-')
</script>

<style scoped>
.dashboard { min-height: calc(100vh - 150px); }

/* ============ 核心指标 ============ */
.kpi-card { position: relative; padding-left: 20px; }
.kpi-icon {
  position: absolute; right: 16px; top: 50%;
  transform: translateY(-50%);
  width: 34px; height: 34px;
  display: grid; place-items: center;
  border-radius: 8px;
  color: var(--kpi);
  background: color-mix(in srgb, var(--kpi) 12%, #fff);
}

/* ============ 面板通用 ============ */
.board-grid {
  display: grid; grid-template-columns: 1.1fr 1fr 1.1fr; gap: 16px; margin-bottom: 16px;
}
.board-grid.two { grid-template-columns: 1fr 1fr; }
.panel-body { padding: 16px 20px 20px; }

/* ============ 横向条形 ============ */
.bars { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
.bars li { display: grid; grid-template-columns: 96px 1fr 52px; align-items: center; gap: 10px; }
.bar-label {
  font-size: 12px; color: var(--muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bar-track { height: 10px; border-radius: 6px; background: var(--el-fill-color-light); overflow: hidden; }
.bar-fill {
  height: 100%; border-radius: 6px;
  background: linear-gradient(90deg, var(--brand), var(--brand-light));
  transition: width 0.6s ease;
}
.bar-fill.gold { background: linear-gradient(90deg, #d97706, #f59e0b); }
.bar-value { text-align: right; font-size: 12px; color: var(--text); font-weight: 600; font-variant-numeric: tabular-nums; }

/* ============ 状态环形图 ============ */
.ring-wrap { position: relative; width: 132px; height: 132px; margin: 0 auto 14px; }
.ring { transform: rotate(-90deg); width: 100%; height: 100%; }
.ring-bg { fill: none; stroke: var(--el-fill-color-light); stroke-width: 12; }
.ring-fg {
  fill: none; stroke: #10b981; stroke-width: 12; stroke-linecap: round;
  transition: stroke-dasharray 0.6s ease;
}
.ring-center {
  position: absolute; inset: 0;
  display: grid; place-content: center; text-align: center;
}
.ring-center strong { font-size: 26px; color: var(--text); font-variant-numeric: tabular-nums; }
.ring-center span { font-size: 11px; color: var(--muted); }
.legend { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 9px 14px; }
.legend li { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); }
.legend i { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.legend b { margin-left: auto; color: var(--text); font-variant-numeric: tabular-nums; }

/* ============ 动态列表 ============ */
.feed { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; max-height: 236px; overflow: auto; }
.feed li { display: flex; align-items: center; gap: 10px; padding: 8px 6px; border-radius: 8px; transition: background 0.15s ease; }
.feed li:hover { background: var(--el-fill-color-light); }
.dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.dot.success { background: #10b981; }
.dot.danger { background: #f87171; }
.feed-main { min-width: 0; flex: 1; }
.feed-main strong, .feed-main small { display: block; }
.feed-main strong { font-size: 12px; color: var(--text); }
.feed-main small {
  margin-top: 2px; font-size: 11px; color: var(--muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.feed time { flex: none; font-size: 11px; color: var(--faint); font-variant-numeric: tabular-nums; }
.empty { color: var(--faint); font-size: 12px; text-align: center; padding: 14px 0; }

/* ============ 响应式 ============ */
@media (max-width: 1200px) {
  .board-grid { grid-template-columns: 1fr 1fr; }
  .board-grid > .content-panel:first-child { grid-column: 1 / -1; }
}
@media (max-width: 900px) {
  .board-grid, .board-grid.two { grid-template-columns: 1fr; }
  .board-grid > .content-panel:first-child { grid-column: auto; }
}
</style>
